import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SearchOutlined, CloseCircleFilled, PlusOutlined, MinusOutlined, EnvironmentOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';

const InnovatorPage = () => {
    const { tab } = useParams();
    const navigate = useNavigate();
    const activeTab = tab || 'startup';

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLetter, setSelectedLetter] = useState(null); // State for A-Z filter
    const [expandedFilters, setExpandedFilters] = useState({
        INDUSTRY: false,
        DISTRICT: false,
        STATE: false,
        CITY: false
    });
    const [selectedFilters, setSelectedFilters] = useState({
        INDUSTRY: [],
        DISTRICT: [],
        STATE: [],
        CITY: []
    });
    const [sortOrder, setSortOrder] = useState('');
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [startups, setStartups] = useState([]);
    const [incubators, setIncubators] = useState([]);
    const [associations, setAssociations] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [academia, setAcademia] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_URL = process.env.REACT_APP_API_BASE_URL || '';

    const toggleFilter = (filterName) => {
        setExpandedFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    const toggleCheckbox = (e, filterType, value) => {
        e.stopPropagation();
        setSelectedFilters(prev => {
            const currentFilters = prev[filterType];
            if (currentFilters.includes(value)) {
                return {
                    ...prev,
                    [filterType]: currentFilters.filter(item => item !== value)
                };
            } else {
                return {
                    ...prev,
                    [filterType]: [...currentFilters, value]
                };
            }
        });
    };

    const clearAllFilters = () => {
        setSelectedFilters({
            INDUSTRY: [],
            DISTRICT: [],
            STATE: [],
            CITY: []
        });
    };

    const hasActiveFilters = Object.values(selectedFilters).some(arr => arr.length > 0);

    // Fetch startups from API
    useEffect(() => {
        const fetchStartups = async () => {
            if (activeTab !== 'startup') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/startups`);
                console.log('Startups API Response:', response.data);
                const formattedData = response.data.map(startup => ({
                    id: startup._id,
                    name: startup.startupName || 'Unknown Startup',
                    stage: startup.stage || 'N/A',
                    location: startup.location || 'Location not specified',
                    industry: startup.industry || 'Not specified',
                    logo: startup.logo ? `${API_URL}/${startup.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: startup.startupName ? startup.startupName.charAt(0).toUpperCase() : 'S',
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Startups:', formattedData);
                setStartups(formattedData);
            } catch (err) {
                console.error('Error fetching startups:', err);
                setError('Failed to load startups. Please try again later.');
                setStartups([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStartups();
    }, [activeTab, API_URL]);

    // Fetch incubators from API
    useEffect(() => {
        const fetchIncubators = async () => {
            if (activeTab !== 'incubator') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/incubator`);
                console.log('Incubators API Response:', response.data);
                const formattedData = response.data.map(incubator => ({
                    id: incubator._id,
                    name: incubator.incubatorName || 'Unknown Incubator',
                    stage: incubator.status || 'Active',
                    location: incubator.address || 'Location not specified',
                    industry: incubator.focusAreas?.join(', ') || 'Not specified',
                    logo: incubator.logo ? `${API_URL}/${incubator.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: incubator.incubatorName ? incubator.incubatorName.charAt(0).toUpperCase() : 'I',
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Incubators:', formattedData);
                setIncubators(formattedData);
            } catch (err) {
                console.error('Error fetching incubators:', err);
                setError('Failed to load incubators. Please try again later.');
                setIncubators([]);
            } finally {
                setLoading(false);
            }
        };

        fetchIncubators();
    }, [activeTab, API_URL]);

    // Fetch associations from API
    useEffect(() => {
        const fetchAssociations = async () => {
            if (activeTab !== 'association') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/association`);
                console.log('Associations API Response:', response.data);
                const formattedData = response.data.map(association => ({
                    id: association._id,
                    name: association.associationName || 'Unknown Association',
                    stage: association.isVerified ? 'Registered' : 'Pending',
                    location: `${association.city || ''}, ${association.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || association.address || 'Location not specified',
                    industry: association.registrationNumber || 'Community',
                    logo: association.logo ? `${API_URL}/${association.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: association.associationName ? association.associationName.charAt(0).toUpperCase() : 'A',
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Associations:', formattedData);
                setAssociations(formattedData);
            } catch (err) {
                console.error('Error fetching associations:', err);
                setError('Failed to load associations. Please try again later.');
                setAssociations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAssociations();
    }, [activeTab, API_URL]);

    // Fetch investors from API
    useEffect(() => {
        const fetchInvestors = async () => {
            if (activeTab !== 'investor') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/investor`);
                console.log('Investors API Response:', response.data);
                const formattedData = response.data.map(investor => ({
                    id: investor._id,
                    name: investor.investorName || investor.fullName || 'Unknown Investor',
                    stage: investor.investorType === 'individual' ? 'Individual' : 'Company',
                    location: `${investor.city || ''}, ${investor.country || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || investor.address || 'Location not specified',
                    industry: investor.investmentFocus?.join(', ') || investor.investmentStage || 'Not specified',
                    logo: investor.logo ? `${API_URL}/${investor.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : investor.profilePicture ? `${API_URL}/${investor.profilePicture.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: (investor.investorName || investor.fullName || 'I').charAt(0).toUpperCase(),
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Investors:', formattedData);
                setInvestors(formattedData);
            } catch (err) {
                console.error('Error fetching investors:', err);
                setError('Failed to load investors. Please try again later.');
                setInvestors([]);
            } finally {
                setLoading(false);
            }
        };

        fetchInvestors();
    }, [activeTab, API_URL]);

    // Fetch academia from API
    useEffect(() => {
        const fetchAcademia = async () => {
            if (activeTab !== 'academia') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/academia`);
                console.log('Academia API Response:', response.data);
                const formattedData = response.data.map(school => ({
                    id: school._id,
                    name: school.schoolName || 'Unknown School',
                    stage: school.status === 'approved' ? 'Active' : 'Pending',
                    location: `${school.district || ''}`.trim() || school.address || 'Location not specified',
                    industry: 'Education',
                    logo: school.logo ? `${API_URL}/${school.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: (school.schoolName || 'A').charAt(0).toUpperCase(),
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Academia:', formattedData);
                setAcademia(formattedData);
            } catch (err) {
                console.error('Error fetching academia:', err);
                setError('Failed to load academia. Please try again later.');
                setAcademia([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAcademia();
    }, [activeTab, API_URL]);

    // Fetch consultants from API
    useEffect(() => {
        const fetchConsultants = async () => {
            if (activeTab !== 'consultant') return;
            
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API_URL}/api/consultants`);
                console.log('Consultants API Response:', response.data);
                const formattedData = response.data.map(consultant => ({
                    id: consultant._id,
                    name: consultant.fullName || 'Unknown Consultant',
                    stage: consultant.status === 'approved' ? 'Active' : 'Pending',
                    location: `${consultant.city || ''}, ${consultant.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || 'Location not specified',
                    industry: Array.isArray(consultant.expertise) ? consultant.expertise.join(', ') : consultant.expertise || 'Not specified',
                    logo: consultant.profilePicture ? `${API_URL}/${consultant.profilePicture.replace(/\\/g, '/').replace(/^\/+/, '')}` : null,
                    logoText: (consultant.fullName || 'C').charAt(0).toUpperCase(),
                    logoColor: getRandomColor()
                }));
                console.log('Formatted Consultants:', formattedData);
                setConsultants(formattedData);
            } catch (err) {
                console.error('Error fetching consultants:', err);
                setError('Failed to load consultants. Please try again later.');
                setConsultants([]);
            } finally {
                setLoading(false);
            }
        };

        fetchConsultants();
    }, [activeTab, API_URL]);

    // Generate random colors for logos
    const getRandomColor = () => {
        const colors = ['#2196F3', '#E91E63', '#FFC107', '#009688', '#FF5722', '#9C27B0', '#673AB7', '#3F51B5'];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    // Generate Alphabet Array A-Z
    const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

    // Tabs configuration
    const tabs = [
        { id: 'startup', label: 'Startups' },
        { id: 'incubator', label: 'Incubators' },
        { id: 'association', label: 'Associations' },
        { id: 'investor', label: 'Investor' },
        { id: 'academia', label: 'Academia' },
        { id: 'consultant', label: 'Consultants' }
    ];

    const getData = () => {
        if (activeTab === 'startup') return startups;
        if (activeTab === 'incubator') return incubators;
        if (activeTab === 'association') return associations;
        if (activeTab === 'investor') return investors;
        if (activeTab === 'academia') return academia;
        if (activeTab === 'consultant') return consultants;
        return [];
    };

    // Generate dynamic filter options from data
    const filterOptions = useMemo(() => {
        const data = getData();
        const industries = new Set();
        const districts = new Set();
        const states = new Set();
        const cities = new Set();

        data.forEach(item => {
            if (item.industry) industries.add(item.industry);
            if (item.location) {
                const parts = item.location.split(',').map(p => p.trim());
                if (parts.length > 0) cities.add(parts[0]);
                if (parts.length > 1) states.add(parts[1]);
            }
        });

        return {
            INDUSTRY: Array.from(industries).sort(),
            DISTRICT: Array.from(districts).sort(),
            STATE: Array.from(states).sort(),
            CITY: Array.from(cities).sort()
        };
    }, [activeTab, startups, incubators, associations, investors, academia, consultants]);

    // Filter and Sort Data
    const processedData = useMemo(() => {
        let data = getData();

        // Search Filter
        if (searchQuery) {
            data = data.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }

        // Industry Filter
        if (selectedFilters.INDUSTRY.length > 0) {
            data = data.filter(item => selectedFilters.INDUSTRY.includes(item.industry));
        }

        // Location Filters (CITY, STATE)
        if (selectedFilters.CITY.length > 0) {
            data = data.filter(item => {
                const city = item.location?.split(',')[0]?.trim();
                return city && selectedFilters.CITY.includes(city);
            });
        }

        if (selectedFilters.STATE.length > 0) {
            data = data.filter(item => {
                const state = item.location?.split(',')[1]?.trim();
                return state && selectedFilters.STATE.includes(state);
            });
        }

        // A-Z Letter Filter
        if (selectedLetter) {
            data = data.filter(item => item.name.toUpperCase().startsWith(selectedLetter));
        }

        // Sort Order
        if (sortOrder) {
            data = [...data].sort((a, b) => {
                if (sortOrder === 'asc') return a.name.localeCompare(b.name);
                if (sortOrder === 'desc') return b.name.localeCompare(a.name);
                return 0;
            });
        }

        return data;
    }, [activeTab, sortOrder, selectedLetter, searchQuery, selectedFilters, startups, incubators, associations, investors, academia, consultants]);

    const azFilterBar = (
        <div style={{
            display: "flex",
            gap: "5px",
            marginBottom: "30px",
            flexWrap: "wrap",
            justifyContent: "center"
        }}>
            {alphabet.map((letter) => {
                const isActive = selectedLetter === letter;
                return (
                    <div
                        key={letter}
                        onClick={() => setSelectedLetter(isActive ? null : letter)}
                        style={{
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: isActive ? "#470172" : "#f1f3f5",
                            color: isActive ? "#fff" : "#495057",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            border: "1px solid transparent",
                            fontFamily: '"Rubik", sans-serif'
                        }}
                        onMouseEnter={(e) => {
                            if (!isActive) {
                                e.currentTarget.style.backgroundColor = "#e9ecef";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isActive) {
                                e.currentTarget.style.backgroundColor = "#f1f3f5";
                            }
                        }}
                    >
                        {letter}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div style={{ fontFamily: '"Rubik", sans-serif', backgroundColor: "#fcfcfc", minHeight: "100vh", marginTop: "30px", paddingBottom: "40px", overflowX: "hidden" }}>
            <style>{`
                @media (max-width: 900px) {
                    .innovator-main {
                        flex-direction: column !important;
                        padding: 16px !important;
                    }
                    .innovator-sidebar {
                        max-width: 100% !important;
                        padding-right: 0 !important;
                    }
                    .innovator-cards-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .innovator-filters-toggle {
                        display: flex !important;
                    }
                    .innovator-filters-body {
                        display: none;
                    }
                    .innovator-filters-body.innovator-filters-open {
                        display: block;
                    }
                    .innovator-az-desktop {
                        display: none;
                    }
                    .innovator-az-mobile {
                        display: block !important;
                    }
                    .innovator-nav-tabs {
                        display: none !important;
                    }
                    .innovator-mobile-title {
                        display: block !important;
                        margin-bottom: 16px;
                    }
                }
                .innovator-filters-toggle {
                    display: none;
                }
                .innovator-az-mobile {
                    display: none;
                }
                .innovator-mobile-title {
                    display: none;
                }
            `}</style>

            {/* Mobile Section Title (fills the space above the Filters box; replaces the tab bar on mobile) */}
            <div className="innovator-mobile-title" style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 20px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#333", margin: 0, fontFamily: '"Rubik", sans-serif' }}>
                    {tabs.find(t => t.id === activeTab)?.label || 'Innovator'}
                </h1>
            </div>

            {/* Main Container */}
            <div className="innovator-main" style={{ display: "flex", maxWidth: "1600px", margin: "0 auto", padding: "20px", flexWrap: "wrap" }}>

                {/* LEFT SIDEBAR - FILTERS */}
                <div className="innovator-sidebar" style={{ width: "100%", maxWidth: "280px", flexShrink: 0, paddingRight: "30px", marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                        <h2 style={{ fontSize: "24px", fontWeight: "700", margin: 0, color: "#333" }}>Filters</h2>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {hasActiveFilters && (
                                <button
                                    onClick={clearAllFilters}
                                    style={{
                                        padding: "6px 12px",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        color: "#470172",
                                        backgroundColor: "transparent",
                                        border: "1px solid #470172",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontFamily: '"Rubik", sans-serif',
                                        transition: "all 0.2s"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#470172";
                                        e.currentTarget.style.color = "white";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#470172";
                                    }}
                                >
                                    Clear All
                                </button>
                            )}
                            <button
                                type="button"
                                className="innovator-filters-toggle"
                                onClick={() => setMobileFiltersOpen((prev) => !prev)}
                                style={{
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    padding: "6px 12px",
                                    background: "transparent",
                                    border: "1px solid #470172",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontFamily: '"Rubik", sans-serif',
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    color: "#470172"
                                }}
                            >
                                {mobileFiltersOpen ? <MinusOutlined style={{ fontSize: "12px" }} /> : <PlusOutlined style={{ fontSize: "12px" }} />}
                                {mobileFiltersOpen ? "Hide" : "Show"}
                            </button>
                        </div>
                    </div>

                    {/* Accordion Filters */}
                    <div className={`innovator-filters-body ${mobileFiltersOpen ? "innovator-filters-open" : ""}`}>
                    {['INDUSTRY', 'DISTRICT', 'STATE', 'CITY'].map((filter) => (
                        <div key={filter} style={{ marginBottom: "20px" }}>
                            <div
                                onClick={() => toggleFilter(filter)}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    marginBottom: expandedFilters[filter] ? "15px" : "0"
                                }}
                            >
                                <span style={{ fontSize: "15px", fontWeight: "600", color: "#555", letterSpacing: "0.5px" }}>{filter}</span>
                                {expandedFilters[filter] ? <MinusOutlined style={{ fontSize: "16px", color: "#470172" }} /> : <PlusOutlined style={{ fontSize: "16px", color: "#470172" }} />}
                            </div>

                            {/* Expanded Content */}
                            {expandedFilters[filter] && (
                                <div style={{ paddingLeft: "5px" }}>
                                    <input
                                        type="text"
                                        placeholder={`Search ${filter}`}
                                        style={{
                                            width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd",
                                            fontSize: "15px", marginBottom: "15px", outline: "none", fontFamily: '"Rubik", sans-serif'
                                        }}
                                    />
                                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        {filterOptions[filter].length > 0 ? (
                                            filterOptions[filter].map(item => {
                                                const isChecked = selectedFilters[filter].includes(item);
                                                return (
                                                    <label key={item} style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }} onClick={(e) => toggleCheckbox(e, filter, item)}>
                                                        <div style={{
                                                            width: "18px",
                                                            height: "18px",
                                                            flexShrink: 0,
                                                            marginTop: "2px",
                                                            border: `2px solid ${isChecked ? '#470172' : '#ccc'}`,
                                                            borderRadius: "3px",
                                                            backgroundColor: isChecked ? "#470172" : "transparent",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            transition: "all 0.2s"
                                                        }}>
                                                            {isChecked && <CheckOutlined style={{ fontSize: "12px", color: "white" }} />}
                                                        </div>
                                                        <span style={{ fontSize: "15px", color: "#666", fontFamily: '"Rubik", sans-serif', wordBreak: "break-word", minWidth: 0 }}>{item}</span>
                                                    </label>
                                                );
                                            })
                                        ) : (
                                            <span style={{ fontSize: "14px", color: "#999", fontStyle: "italic" }}>No {filter.toLowerCase()} options available</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!expandedFilters[filter] && <div style={{ height: "1px", backgroundColor: "#eee", marginTop: "15px" }}></div>}
                        </div>
                    ))}

                    {/* ALPHABETICAL FILTER BAR (mobile, inside Filters container) */}
                    <div className="innovator-az-mobile">
                        <div style={{ height: "1px", backgroundColor: "#eee", marginBottom: "20px" }}></div>
                        <span style={{ fontSize: "15px", fontWeight: "600", color: "#555", letterSpacing: "0.5px", display: "block", marginBottom: "15px" }}>A-Z</span>
                        {azFilterBar}
                    </div>
                    </div>
                </div>

                {/* RIGHT CONTENT AREA */}
                <div style={{ flex: 1, minWidth: "0" }}>

                    {/* Search Bar Row */}
                    <div style={{ display: "flex", gap: "15px", marginBottom: "30px", flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: "250px", position: "relative" }}>
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "14px 45px 14px 14px",
                                    borderRadius: "4px",
                                    border: "1px solid #ddd",
                                    fontSize: "16px",
                                    outline: "none",
                                    fontFamily: '"Rubik", sans-serif',
                                    boxSizing: "border-box"
                                }}
                            />
                            {searchQuery && (
                                <CloseCircleFilled
                                    onClick={() => setSearchQuery('')}
                                    style={{
                                        position: "absolute",
                                        right: "18px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#470172",
                                        fontSize: "16px",
                                        cursor: "pointer",
                                        zIndex: 1
                                    }}
                                />
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "4px", padding: "0 15px", backgroundColor: "white", minWidth: "220px" }}>
                            <span style={{ fontSize: "16px", color: "#666", marginRight: "10px", fontFamily: '"Rubik", sans-serif', whiteSpace: "nowrap" }}>Sort By</span>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                style={{ border: "none", outline: "none", fontSize: "16px", color: "#333", flex: 1, backgroundColor: "transparent", fontWeight: "500", fontFamily: '"Rubik", sans-serif', cursor: "pointer" }}
                            >
                                <option value="">Select</option>
                                <option value="asc">Ascending (A-Z)</option>
                                <option value="desc">Descending (Z-A)</option>
                            </select>
                        </div>
                    </div>

                    {/* ALPHABETICAL FILTER BAR (desktop) */}
                    <div className="innovator-az-desktop">
                        {azFilterBar}
                    </div>

                    {/* Navigation Tabs */}
                    <div className="innovator-nav-tabs" style={{
                        display: "flex",
                        gap: "40px",
                        borderBottom: "1px solid #eee",
                        marginBottom: "30px",
                        paddingBottom: "1px",
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch"
                    }}>
                        {tabs.map((tabItem) => {
                            const isActive = activeTab === tabItem.id;
                            return (
                                <div
                                    key={tabItem.id}
                                    onClick={() => navigate(`/innovator/${tabItem.id}`)}
                                    style={{
                                        cursor: "pointer",
                                        paddingBottom: "15px",
                                        position: "relative",
                                        color: isActive ? "#333" : "#888",
                                        fontWeight: isActive ? "700" : "400",
                                        fontSize: "17px",
                                        fontFamily: '"Rubik", sans-serif',
                                        whiteSpace: "nowrap",
                                        flexShrink: 0
                                    }}
                                >
                                    {tabItem.label}
                                    {isActive && (
                                        <div style={{
                                            position: "absolute",
                                            bottom: "-1px",
                                            left: 0,
                                            width: "100%",
                                            height: "3px",
                                            backgroundColor: "#470172"
                                        }}></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Cards Grid */}
                    <div className="innovator-cards-grid" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: "20px"
                    }}>
                        {loading ? (
                            <div style={{ gridColumn: "1/-1", padding: "80px 40px", textAlign: "center", color: "#888" }}>
                                <LoadingOutlined style={{ fontSize: "48px", color: "#470172", marginBottom: "20px" }} />
                                <div style={{ fontSize: "18px", fontFamily: '"Rubik", sans-serif' }}>Loading startups...</div>
                            </div>
                        ) : error ? (
                            <div style={{ gridColumn: "1/-1", padding: "80px 40px", textAlign: "center", color: "#d32f2f" }}>
                                <div style={{ fontSize: "48px", marginBottom: "20px" }}>⚠️</div>
                                <div style={{ fontSize: "18px", fontFamily: '"Rubik", sans-serif' }}>{error}</div>
                            </div>
                        ) : processedData.length > 0 ? (
                            processedData.map((item) => (
                                <div key={item.id} style={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    border: "1px solid #eee",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.02)",
                                    overflow: "hidden",
                                    transition: "transform 0.2s, box-shadow 0.2s",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column"
                                }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-3px)";
                                        e.currentTarget.style.boxShadow = "0 8px 15px rgba(0,0,0,0.08)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.02)";
                                    }}
                                >
                                    {/* Card Top Section (Logo + Details) */}
                                    <div style={{ padding: "20px", display: "flex", gap: "15px", alignItems: "flex-start" }}>

                                        {/* Logo with Image or Fallback */}
                                        <div style={{
                                            width: "60px",
                                            height: "60px",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            flexShrink: 0,
                                            border: "1px solid #f0f0f0",
                                            backgroundColor: item.logo ? "transparent" : item.logoColor,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            {item.logo ? (
                                                <img
                                                    src={item.logo}
                                                    alt={item.name}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = `<div style="color: white; font-size: 24px; font-weight: 700; font-family: 'Rubik', sans-serif;">${item.logoText}</div>`;
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    color: "white",
                                                    fontSize: "24px",
                                                    fontWeight: "700",
                                                    fontFamily: '"Rubik", sans-serif'
                                                }}>
                                                    {item.logoText}
                                                </div>
                                            )}
                                        </div>

                                        {/* Text Details */}
                                        <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                                            <h3 style={{
                                                fontSize: "16px",
                                                fontWeight: "700",
                                                color: "#333",
                                                textTransform: "uppercase",
                                                margin: "0 0 10px 0",
                                                lineHeight: "1.4",
                                                fontFamily: '"Rubik", sans-serif',
                                                textAlign: "left",
                                                wordBreak: "break-word"
                                            }}>
                                                {item.name}
                                            </h3>

                                            <div style={{
                                                fontSize: "15px",
                                                color: "#555",
                                                marginBottom: "10px",
                                                fontFamily: '"Rubik", sans-serif',
                                                textAlign: "left"
                                            }}>
                                                {item.stage}
                                            </div>

                                            <div style={{
                                                fontSize: "15px",
                                                color: "#888",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontFamily: '"Rubik", sans-serif',
                                                textAlign: "left"
                                            }}>
                                                <EnvironmentOutlined style={{ fontSize: "16px" }} />
                                                {item.location}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Footer (Category + View Details Button) */}
                                    <div style={{
                                        marginTop: "auto",
                                        padding: "14px 20px",
                                        borderTop: "1px solid #f5f5f5",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "10px"
                                    }}>
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#777",
                                            fontFamily: '"Rubik", sans-serif',
                                            flex: 1
                                        }}>
                                            {item.industry}
                                        </div>
                                        <button
                                            onClick={() => navigate(`/${activeTab}/${item.id}`)}
                                            style={{
                                                padding: "8px 16px",
                                                backgroundColor: "#470172",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                fontFamily: '"Rubik", sans-serif',
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                whiteSpace: "nowrap"
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#5a0192";
                                                e.currentTarget.style.transform = "translateY(-1px)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "#470172";
                                                e.currentTarget.style.transform = "translateY(0)";
                                            }}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ gridColumn: "1/-1", padding: "80px 40px", textAlign: "center", color: "#888" }}>
                                <div style={{ fontSize: "48px", marginBottom: "20px" }}>📭</div>
                                <div style={{ fontSize: "18px", fontFamily: '"Rubik", sans-serif', marginBottom: "8px" }}>
                                    {selectedLetter 
                                        ? `No items found starting with "${selectedLetter}"` 
                                        : `No ${tabs.find(t => t.id === activeTab)?.label.toLowerCase() || 'items'} available yet`
                                    }
                                </div>
                                {!selectedLetter && (
                                    <div style={{ fontSize: "14px", color: "#aaa", fontFamily: '"Rubik", sans-serif' }}>
                                        Check back later for updates
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default InnovatorPage;